import {AppBar, MenuItem, Stack, Toolbar, css, styled} from '@mui/material';
import advanceFlower from 'presentation/assets/images/advance-flower.webp';
import advanceBackground from 'presentation/assets/images/advance-header-bg.webp';
import infinityFlower from 'presentation/assets/images/infinity-flower.webp';
import infinityBackground from 'presentation/assets/images/infinity-header-bg.webp';
import notredameSaudeFlower from 'presentation/assets/images/notredame-saude-flower.webp';
import notredameSaudeBackground from 'presentation/assets/images/notredame-saude-header-bg.webp';
import {StyledAppBarProps, StyledRouteContainerProps, StyledToolbarProps} from './types';

const getBackgroundColor = (themeName: string) => {
	switch (themeName) {
		case 'infinity':
		case 'advance':
		case 'premium':
			return css`
				background: ${({theme}) => theme.palette.other.planColor};
			`;

		default:
			return css`
				background: ${({theme}) => theme.palette.primary.main};
			`;
	}
};

const getBackgroundImages = (themeName: string) => {
	switch (themeName) {
		case 'infinity':
			return css`
				background: url(${infinityFlower}), url(${infinityBackground});
			`;
		case 'advance':
			return css`
				background: url(${advanceFlower}), url(${advanceBackground});
			`;
		case 'premium':
			return css`
				background: url(${notredameSaudeFlower}), url(${notredameSaudeBackground});
			`;
		default:
			return css`
				background: ${({theme}) => theme.palette.primary.main};
			`;
	}
};

const getBackground = (shouldShowBackgroundAndLogo: boolean, themeName: string) => {
	if (shouldShowBackgroundAndLogo) {
		return getBackgroundImages(themeName);
	}

	return getBackgroundColor(themeName);
};

export const StyledAppBar = styled(AppBar)<StyledAppBarProps>`
	${({theme, $shouldShowBackgroundAndLogo}) =>
		getBackground($shouldShowBackgroundAndLogo, theme.name)};
	background-repeat: no-repeat, no-repeat;

	background-position:
		calc(100% + 30px) -15px,
		center;
	background-size:
		120px 120px,
		cover;

	${({theme, $isHomePage}) => css`
		${theme.breakpoints.down('md')} {
			background-position:
				${$isHomePage ? 'calc(100% + 30px) top' : 'calc(100%) top'}, center;

			background-size: ${$isHomePage ? '180px 180px' : '70px 70px'}, cover;
		}
	`}
`;

const StyledToolbar = styled(Toolbar)<StyledToolbarProps>`
	flex-direction: row;
	align-items: center;
	justify-content: space-between;
	padding: ${({$showLogo}) => ($showLogo ? 16 : 8)}px;
	gap: 24px;
	background-color: transparent;

	${({theme}) => theme.breakpoints.up('sm')} {
		padding-block: ${({$isBreadcrumb}) => ($isBreadcrumb ? 24 : 16)}px;
		padding-inline: 32px;
	}
`;

const StyledRouteContainer = styled(Stack)<StyledRouteContainerProps>`
	flex-direction: row;
	align-items: center;
	gap: 12px;
	justify-content: space-between;
	width: 100%;

	${({theme}) => theme.breakpoints.down(395)} {
		gap: 6px;
	}

	${({$isBreadcrumb}) =>
		$isBreadcrumb
			? css`
					flex-direction: column;
					align-items: start;
					gap: 8px;

					.MuiBreadcrumbs-root {
						padding-left: 50px;
					}
				`
			: undefined}
`;

const StyledMenuItem = styled(MenuItem)`
	gap: 8px;
	padding: 16px;
	color: ${({theme}) => theme.palette.text.secondary};
`;

const StyledMenuItemBeneficiary = styled(StyledMenuItem)`
	padding: 0;

	&.Mui-disabled {
		opacity: 1;
	}

	& > .MuiTouchRipple-root {
		display: none;
	}
`;

const StyledMenuItemLogout = styled(StyledMenuItem)`
	padding: 24px;
`;

const StyledMenuContainer = styled(Stack)`
	flex-direction: row;
	align-items: center;
	justify-content: flex-end;
	gap: 12px;
`;

export {
	StyledMenuContainer,
	StyledMenuItem,
	StyledMenuItemBeneficiary,
	StyledMenuItemLogout,
	StyledRouteContainer,
	StyledToolbar,
};
